import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, ArrowLeftToLine, Clock as ClockIcon } from 'lucide-react';
import { startPoll } from '../../lib/poll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisitorAlerts() {
  const [visits, setVisits] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => startPoll(fetchAll, 45_000), []);  // v2.6.4: was 8 s

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/visitors/my-active`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setVisits(d || []);
    } catch (e) {}
  };

  const submitMeetingDone = async (id) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/visitors/${id}/meeting-done`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ remarks })
      });
      if (r.ok) {
        setEditingId(null);
        setRemarks('');
        fetchAll();
      } else {
        const d = await r.json();
        alert(d.detail || 'Failed');
      }
    } finally { setBusy(false); }
  };

  const resendExitOtp = async (id) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/visitors/${id}/resend-exit-otp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        alert('Exit OTP re-sent to visitor on WhatsApp.');
      } else {
        alert(d.detail || 'Could not re-send.');
      }
    } finally { setBusy(false); }
  };

  if (visits.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-purple-200" data-testid="visitor-alerts">
      <div className="flex items-center space-x-2 mb-3">
        <Users className="text-purple-600" size={20} />
        <h3 className="font-semibold">Your Visitors ({visits.length})</h3>
      </div>
      <div className="space-y-3">
        {visits.map(v => (
          <div key={v.id} className="border-2 border-gray-200 rounded-xl p-3" data-testid={`visitor-alert-${v.id}`}>
            <div className="flex items-start space-x-3">
              {v.visitor_photo && (
                <img src={v.visitor_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
              )}
              <div className="flex-1">
                <p className="font-bold">{v.visitor_name}</p>
                <p className="text-xs text-gray-500">{v.visitor_company} • {v.visitor_mobile}</p>
                {v.purpose && <p className="text-xs text-gray-600 mt-1">📌 {v.purpose}</p>}
              </div>
              <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                v.status === 'pending_in' ? 'bg-orange-100 text-orange-700' :
                v.status === 'inside' ? 'bg-blue-100 text-blue-700' :
                v.status === 'meeting_done' ? 'bg-green-100 text-green-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {v.status.replace('_', ' ')}
              </span>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
              <span className="flex items-center space-x-1">
                {v.status === 'pending_in' && <><ArrowLeftToLine size={12} /><span>At gate</span></>}
                {v.status === 'inside' && <><ClockIcon size={12} /><span>In meeting</span></>}
              </span>
              <span className="font-mono font-bold text-amber-700">{v.entry_code}</span>
            </div>

            {v.status === 'inside' && (
              editingId === v.id ? (
                <div className="mt-3 space-y-2">
                  <input
                    type="text" placeholder="Meeting remarks (optional)"
                    value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    data-testid={`remarks-${v.id}`}
                  />
                  <div className="flex space-x-2">
                    <button onClick={() => submitMeetingDone(v.id)} disabled={busy} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center space-x-1" data-testid={`confirm-meeting-done-${v.id}`}>
                      <CheckCircle size={14} /><span>Confirm</span>
                    </button>
                    <button onClick={() => setEditingId(null)} className="px-3 py-2 bg-gray-200 rounded-lg text-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setEditingId(v.id); setRemarks(''); }} className="mt-3 w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center justify-center space-x-1" data-testid={`meeting-done-btn-${v.id}`}>
                  <CheckCircle size={14} /><span>Meeting Over</span>
                </button>
              )
            )}
            {v.status === 'meeting_done' && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-xs text-green-700 flex-1">
                  ✓ Meeting marked complete. Exit OTP <span className="font-mono font-bold">{v.exit_otp || '…'}</span> sent.
                </p>
                <button
                  onClick={() => resendExitOtp(v.id)}
                  disabled={busy}
                  className="text-xs text-purple-700 hover:text-purple-900 underline font-medium disabled:opacity-50 whitespace-nowrap"
                  data-testid={`resend-exit-otp-${v.id}`}
                >Re-send</button>
              </div>
            )}
            {v.status === 'pending_out' && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-xs text-purple-700 flex-1">
                  ⏳ Visitor at gate. Exit OTP <span className="font-mono font-bold">{v.exit_otp || '…'}</span>.
                </p>
                <button
                  onClick={() => resendExitOtp(v.id)}
                  disabled={busy}
                  className="text-xs text-purple-700 hover:text-purple-900 underline font-medium disabled:opacity-50 whitespace-nowrap"
                  data-testid={`resend-exit-otp-${v.id}`}
                >Re-send</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
